import { useEffect, useRef, useState, useId } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X, MapPin, Calendar, Clock, Users, Building, Plus } from 'lucide-react';
import { Toast } from '@/components/ui/Toast/Toast';
import { ConfirmModal } from '@/components/ui/ConfirmModal/ConfirmModal';
import { DurationStepper } from '@/components/ui/DurationStepper';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';
import { useSwipeToDismiss } from '@/hooks/useSwipeToDismiss';
import { formatDateRangeDetailed } from '@/utils/dateFormat';
import { EventImage } from '@/components/common/EventImage';
import { formatEventTimes, formatDepartments, showsYourShifts } from './utils';
import { ShiftList } from '@/components/common/ShiftList';
import { useEventShifts } from '@/hooks/useEventShifts';
import { formatShiftLabel, type EventShift } from '@/utils/eventShifts';
import { changeShift, joinShifts, leaveShift } from '@/utils/eventShiftsApi';
import { EventPreviewYourShifts } from './EventPreviewYourShifts';
import { ChangeShiftModal } from './ChangeShiftModal';
import { PickShiftsModal } from './PickShiftsModal';
import { useEventPreviewData } from './useEventPreviewData';
import { useLeaveEvent } from '@/hooks/useLeaveEvent';
import type { EventPreviewModalProps } from './types';
import { useUser } from '@/constants/UserContext';
import { EventPreviewStatsDesktop, EventPreviewStatsMobile } from './EventPreviewStats';
import { EventPreviewVolunteerSection } from './EventPreviewVolunteerSection';
import { EventPreviewOrgSection } from './EventPreviewOrgSection';
import { ExpandableClampText } from '@/pages/Volunteers/components/ExpandableClampText';

export { type ProfileEvent } from './types';

export default function EventPreviewModal({
  visible,
  onClose,
  isOrganization = false,
  hideOrganizationInfo = false,
  fromInviteLink = false,
  onJoinEvent,
  onNavigateToVolunteers,
  onOpenTasks,
  onOpenVolunteerTasks,
  onLeftEvent,
  scrollToShifts = false,
  event,
}: EventPreviewModalProps) {
  useModalScrollLock(visible);
  const { activeAdminOrgId } = useUser();
  const isOrgView = isOrganization || Boolean(activeAdminOrgId);
  const addHoursDescriptionFieldId = useId();

  const data = useEventPreviewData(visible, event, {
    isOrganization: isOrgView,
    fromInviteLink,
    onJoinEvent,
    onClose,
  });

  const {
    organization,
    stats,
    isAlreadyVolunteer,
    isAcceptedVolunteer,
    volunteerInfo,
    currentEvent,
    incompleteTasksCount,
    refreshVolunteerState,
    toast,
    hideToast,
    showToast,
    user,
    handleShareEvent,
    handleJoinEvent,
    onClose: closeModal,
    loading,
    creatingHours,
    createVolunteerHoursRequest,
  } = data;

  const {
    shifts: eventShifts,
    refetch: refetchShifts,
    loadedFor: shiftsLoadedFor,
  } = useEventShifts(currentEvent?.id, visible);

  // Opened from the card's Shifts button: once this event and its shifts have loaded, scroll to
  // the volunteer's own shifts -- where Change, Leave and Pick more are -- or, if that block is
  // not shown, to the event's shift list, or to the event details that say it has none.
  // Waiting on the shifts matters: the first render has none yet, and scrolling then would
  // stop short at the fallback.
  const yourShiftsRef = useRef<HTMLDivElement>(null);
  const shiftListRef = useRef<HTMLDivElement>(null);
  const eventInfoRef = useRef<HTMLDivElement>(null);
  const scrolledForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) {
      scrolledForRef.current = null;
      return;
    }
    const eventId = currentEvent?.id;
    if (!scrollToShifts || !eventId || loading || shiftsLoadedFor !== eventId) return;
    if (scrolledForRef.current === eventId) return;
    const target = yourShiftsRef.current ?? shiftListRef.current ?? eventInfoRef.current;
    if (!target) return;
    scrolledForRef.current = eventId;
    // A frame later, so the section is laid out before we measure where it is.
    requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [visible, scrollToShifts, currentEvent?.id, loading, shiftsLoadedFor, eventShifts]);
  const [pendingLeaveShiftId, setPendingLeaveShiftId] = useState<string | null>(null);
  const [changingFrom, setChangingFrom] = useState<EventShift | null>(null);
  const [movingShift, setMovingShift] = useState(false);
  const [pickingShifts, setPickingShifts] = useState(false);
  const [joiningShifts, setJoiningShifts] = useState(false);
  const [leavingShift, setLeavingShift] = useState(false);
  const pendingLeaveShift = eventShifts.find((s) => s.id === pendingLeaveShiftId) ?? null;

  // Dropping a shift also tells the organizer, so they can fill the spot again.
  const confirmLeaveShift = async () => {
    if (!pendingLeaveShiftId || leavingShift) return;
    setLeavingShift(true);
    const result = await leaveShift(pendingLeaveShiftId);
    setLeavingShift(false);
    setPendingLeaveShiftId(null);
    if (!result.ok) {
      showToast(result.error, 'error');
      return;
    }
    showToast(result.left ? 'You left the shift' : 'You are not on that shift', result.left ? 'success' : 'info');
    void refetchShifts();
  };

  // Shifts that filled up or started while the picker was open are reported, not joined.
  const confirmPickShifts = async (shiftIds: string[]) => {
    if (!currentEvent?.id || joiningShifts) return;
    setJoiningShifts(true);
    const result = await joinShifts(currentEvent.id, shiftIds);
    setJoiningShifts(false);
    void refetchShifts();
    if (!result.ok) {
      showToast(result.error, 'error');
      return;
    }
    setPickingShifts(false);
    const missed = result.full.length + result.started.length;
    if (result.added.length === 0) {
      showToast('Those shifts filled up or started before you could join them.', 'error');
    } else if (missed > 0) {
      showToast(
        `Added ${result.added.length} ${result.added.length === 1 ? 'shift' : 'shifts'}; ${missed} ${missed === 1 ? 'was' : 'were'} no longer available.`,
        'info',
      );
    } else {
      showToast(result.added.length === 1 ? 'Shift added' : `${result.added.length} shifts added`, 'success');
    }
  };

  // One call: if the new shift filled up meanwhile, the volunteer keeps the one they had.
  const confirmChangeShift = async (toShiftId: string) => {
    if (!changingFrom || movingShift) return;
    setMovingShift(true);
    const result = await changeShift(changingFrom.id, toShiftId);
    setMovingShift(false);
    if (!result.ok) {
      showToast(result.error, 'error');
      // A refused move usually means the list is stale (the shift filled up), so refresh it.
      void refetchShifts();
      return;
    }
    const target = eventShifts.find((s) => s.id === toShiftId);
    setChangingFrom(null);
    showToast(target ? `Moved to ${formatShiftLabel(target)}` : 'Shift changed', 'success');
    void refetchShifts();
  };

  // Leaving and withdrawing a pending application are the same server-side operation, so
  // the org is notified either way. See src/utils/leaveEvent.ts.
  const { pendingEvent, leaving, requestLeave, confirmLeave, cancelLeave } = useLeaveEvent(
    showToast,
    (eventId) => {
      void refreshVolunteerState();
      // Leaving frees the volunteer's shifts (volunteers_release_shifts trigger).
      void refetchShifts();
      onLeftEvent?.(eventId);
    }
  );

  const [showAddHoursModal, setShowAddHoursModal] = useState(false);
  const [addHoursMinutes, setAddHoursMinutes] = useState(60);
  const [addHoursDescription, setAddHoursDescription] = useState<string>('');

  // Bottom-sheet behavior on mobile: slide-up entry + swipe-down to dismiss.
  const { sheetRef, scrollRef, dragY, dragging, dismissing, requestDismiss } =
    useSwipeToDismiss(closeModal, visible && Boolean(event));

  if (!visible || !event) return null;

  const isEventFull = Boolean(
    currentEvent?.slots != null &&
      currentEvent?.volunteerCount != null &&
      currentEvent.volunteerCount >= currentEvent.slots
  );
  const isEventEnded = currentEvent?.is_active === false;
  const statusText =
    currentEvent?.status === 'Scheduled'
      ? formatDateRangeDetailed(currentEvent?.start_date ?? null, currentEvent?.end_date ?? null)
      : isEventEnded
        ? 'ENDED'
        : (currentEvent?.status || 'Active');

  return (
    <>
      <Flex
        position="fixed"
        inset="0"
        zIndex={1000}
        align={{ base: 'flex-start', md: 'center' }}
        justify="center"
        overflowY="auto"
        p={{ base: '0', md: '5' }}
      >
        <Box
          position="fixed"
          inset="0"
          bg="blackAlpha.500"
          backdropFilter="blur(4px)"
          onClick={requestDismiss}
          css={{ animation: 'fadeIn 0.2s ease-out' }}
          style={{
            opacity: dismissing ? 0 : 1,
            transition: 'opacity 0.25s ease-in',
          }}
        />
        <Box
          ref={sheetRef}
          position="relative"
          bg="white"
          w="full"
          boxShadow="2xl"
          display="flex"
          flexDirection="column"
          borderTopRadius="3xl"
          borderRadius={{ md: '3xl' }}
          maxW={{ base: 'full', md: '5xl' }}
          css={{
            animation: { base: 'slideUpFromBottom 0.3s ease-out', md: 'none' },
          }}
          style={{
            // Keep the sheet below the notch/Dynamic Island (status bar overlays
            // the webview on native): never rise above safe-area-inset-top.
            maxHeight: 'calc(100vh - max(5vh, calc(env(safe-area-inset-top, 0px) + 12px)))',
            marginTop: 'max(5vh, calc(env(safe-area-inset-top, 0px) + 12px))',
            transform: dismissing
              ? 'translateY(100%)'
              : dragY > 0
                ? `translateY(${dragY}px)`
                : undefined,
            transition: dragging ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {/* Header */}
          <Box
            position={{ base: 'sticky', md: 'static' }}
            top="0"
            bg="white"
            borderTopRadius="3xl"
            zIndex={10}
            borderBottom="1px solid"
            borderColor="volyx.borderLight"
          >
            {/* Drag handle — signals the sheet is swipeable on mobile */}
            <Flex display={{ base: 'flex', md: 'none' }} justify="center" pt="2" aria-hidden>
              <Box w="36px" h="4px" borderRadius="full" bg="volyx.mediumGray" />
            </Flex>
            <Flex
              align="center"
              justify="space-between"
              px={{ base: '4', md: '5' }}
              py={{ base: '3', md: '4' }}
            >
              <Text
                as="h2"
                fontSize={{ base: 'md', md: 'xl' }}
                fontWeight="bold"
                color="volyx.text"
                m="0"
              >
                Event Details
              </Text>
              <chakra.button
                onClick={requestDismiss}
                p="1"
                _hover={{ bg: 'volyx.lightGray' }}
                borderRadius="md"
                transition="color 0.15s, background-color 0.15s"
                border="none"
                bg="transparent"
                cursor="pointer"
                aria-label="Close"
              >
                <X size={24} color="var(--color-text)" />
              </chakra.button>
            </Flex>
          </Box>

          {/* Content */}
          <Box
            ref={scrollRef}
            overflowY="auto"
            flex="1"
            display={{ base: 'block', md: 'grid' }}
            gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            gap={{ base: '0', md: '6' }} // 6 * 4px = 24px
            p={{ base: '0', md: '6' }} // 6 * 4px = 24px
          >
            {/* Left Column */}
            <Box
              display={{ base: 'block', md: 'flex' }}
              flexDirection="column"
              gap={{ base: '0', md: '4' }} // 4 * 4px = 16px
            >
              <Box position="relative" h={{ base: '64', md: '280px' }} mb={{ base: '4', md: '0' }}>
                {loading ? (
                  <Box
                    w="full"
                    h="full"
                    bg="volyx.lightGray"
                    borderTopRadius={{ base: '3xl', md: '2xl' }}
                    borderRadius={{ md: '2xl' }}
                    className="animate-pulse"
                  />
                ) : (
                  <>
                    <EventImage
                      stored={currentEvent?.image}
                      alt={currentEvent?.name || 'Event'}
                      w="full"
                      h="full"
                      objectFit="cover"
                      borderTopRadius={{ base: '3xl', md: '2xl' }}
                      borderRadius={{ md: '2xl' }}
                    />
                    {isEventEnded && (
                      <Box
                        position="absolute"
                        inset="0"
                        bg="rgba(107,114,128,0.7)"
                        borderTopRadius={{ base: '3xl', md: '2xl' }}
                        borderRadius={{ md: '2xl' }}
                      />
                    )}
                    <Box position="absolute" top="4" left="4">
                      <Box bg="blackAlpha.700" px="3" py="1.5" borderRadius="2xl">
                        <Text as="span" color="white" fontSize={{ base: 'xs', md: 'sm' }} fontWeight="semibold">
                          {statusText}
                        </Text>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>

              <Box px={{ base: '5', md: '0' }} mb={{ base: '5', md: '0' }}>
                {loading ? (
                  <>
                    <Box h="7" w="75%" bg="volyx.lightGray" borderRadius="md" className="animate-pulse" mb="3" />
                    <Box h="4" w="50%" bg="volyx.lightGray" borderRadius="md" className="animate-pulse" />
                  </>
                ) : (
                  <>
                    <Text
                      as="h3"
                      fontSize={{ base: '2xl', md: '3xl' }}
                      fontWeight="bold"
                      color="volyx.text"
                      mb="2"
                      lineClamp={2}
                    >
                      {currentEvent?.name}
                    </Text>
                    <Flex align="center" gap="1.5" color="volyx.textSecondary" mb="4">
                      <MapPin size={18} />
                      <Text as="span" fontSize={{ base: 'md', md: 'lg' }}>{currentEvent?.location}</Text>
                    </Flex>
                  </>
                )}

                <EventPreviewStatsDesktop stats={stats} currentEvent={currentEvent} loading={loading} />
              </Box>
            </Box>

            {/* Right Column */}
            <Box>
              <EventPreviewStatsMobile stats={stats} currentEvent={currentEvent} loading={loading} />

              <EventPreviewVolunteerSection
                loading={loading}
                isOrganization={isOrgView}
                isEventEnded={isEventEnded}
                isAlreadyVolunteer={isAlreadyVolunteer}
                isAcceptedVolunteer={isAcceptedVolunteer}
                volunteerInfo={volunteerInfo}
                incompleteTasksCount={incompleteTasksCount}
                currentEvent={currentEvent}
                onOpenVolunteerTasks={onOpenVolunteerTasks}
                onOpenAddHours={() => setShowAddHoursModal(true)}
                onCloseModal={requestDismiss}
              />

              {showsYourShifts({ shifts: eventShifts, loading, isEventEnded, isAcceptedVolunteer, isOrgView }) && (
                <Box ref={yourShiftsRef} scrollMarginTop="4" data-testid="your-shifts">
                  <EventPreviewYourShifts
                    shifts={eventShifts}
                    onChange={(shift) => setChangingFrom(shift)}
                    onLeave={(shift) => setPendingLeaveShiftId(shift.id)}
                    onPickMore={() => setPickingShifts(true)}
                    disabled={leavingShift || movingShift || joiningShifts}
                  />
                </Box>
              )}

              {/* Organization Quick Actions */}
              {currentEvent?.id && isOrgView && (isOrganization ? currentEvent?.created_by === user?.id : currentEvent?.created_by === activeAdminOrgId) && (
                <Box px={{ base: '5', md: '0' }} mb="6">
                  <Text as="h4" fontSize="xl" fontWeight="bold" color="volyx.text" mb="4">Quick Actions</Text>
                  <Flex gap="3">
                    {onNavigateToVolunteers && (
                      <chakra.button
                        onClick={() => {
                          if (currentEvent?.id && currentEvent?.name) {
                            requestDismiss();
                            setTimeout(() => onNavigateToVolunteers(currentEvent.id, currentEvent.name), 300);
                          }
                        }}
                        flex="1"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap="2"
                        borderRadius="2xl"
                        py="3.5"
                        px="5"
                        border="1px solid"
                        borderColor="#007AFF"
                        bg="#007AFF"
                        color="white"
                        fontWeight="semibold"
                        fontSize="md"
                        cursor="pointer"
                        outline="none"
                        _focus={{ outline: 'none' }}
                        transition="color 0.15s, background-color 0.15s"
                        boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                        _hover={{ bg: '#0063CC' }}
                      >
                        <Users size={20} />
                        <Text as="span">Manage</Text>
                      </chakra.button>
                    )}
                    {onOpenTasks && (
                      <chakra.button
                        onClick={() => {
                          if (currentEvent?.id) {
                            requestDismiss();
                            setTimeout(() => onOpenTasks(currentEvent.id), 300);
                          }
                        }}
                        flex="1"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap="2"
                        borderRadius="2xl"
                        py="3.5"
                        px="5"
                        border="1px solid"
                        borderColor="#007AFF"
                        bg="#007AFF"
                        color="white"
                        fontWeight="semibold"
                        fontSize="md"
                        cursor="pointer"
                        outline="none"
                        _focus={{ outline: 'none' }}
                        transition="color 0.15s, background-color 0.15s"
                        boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                        _hover={{ bg: '#0063CC' }}
                      >
                        <Plus size={20} />
                        <Text as="span">Add Tasks</Text>
                      </chakra.button>
                    )}
                  </Flex>
                </Box>
              )}

              {/* Event Details */}
              <Box ref={eventInfoRef} scrollMarginTop="4" px={{ base: '5', md: '0' }} mb="6">
                <Text as="h4" fontSize="xl" fontWeight="bold" color="volyx.text" mb="4">Event Information</Text>
                {loading ? (
                  <>
                    <Box mb="5">
                      <Box h="4" w="33%" bg="volyx.lightGray" borderRadius="md" />
                      <Box mt="2" h="3" w="full" bg="volyx.lightGray" borderRadius="md" />
                      <Box mt="2" h="3" w="83%" bg="volyx.lightGray" borderRadius="md" />
                    </Box>
                    <Flex align="center" gap="3" mb="3">
                      <Box w="4" h="4" bg="volyx.lightGray" borderRadius="full" />
                      <Box h="3" w="66%" bg="volyx.lightGray" borderRadius="md" />
                    </Flex>
                    <Flex align="center" gap="3" mb="3">
                      <Box w="4" h="4" bg="volyx.lightGray" borderRadius="full" />
                      <Box h="3" w="50%" bg="volyx.lightGray" borderRadius="md" />
                    </Flex>
                    <Flex align="center" gap="3">
                      <Box w="4" h="4" bg="volyx.lightGray" borderRadius="full" />
                      <Box h="3" w="50%" bg="volyx.lightGray" borderRadius="md" />
                    </Flex>
                  </>
                ) : (
                  <>
                    <Box mb="5">
                      <Text as="h5" fontSize="md" fontWeight="semibold" color="volyx.text" mb="2">Description</Text>
                      <ExpandableClampText
                        text={
                          typeof currentEvent?.description === 'string' && currentEvent.description.trim().length > 0
                            ? currentEvent.description
                            : 'No description provided yet.'
                        }
                        lines={4}
                        fontSize="sm"
                        color="volyx.textSecondary"
                        lineHeight="1.4"
                        moreLabel="More"
                        lessLabel="Less"
                      />
                    </Box>
                    <Flex align="center" gap="3" mb="3">
                      <Box as={Calendar} boxSize="16px" color="volyx.textSecondary" flexShrink={0} />
                      <Text as="span" fontSize="sm" color="volyx.text">
                        {currentEvent?.start_date || currentEvent?.end_date
                          ? `${formatDateRangeDetailed(
                              currentEvent?.start_date ?? null,
                              currentEvent?.end_date ?? null
                            )}${isEventEnded ? ' (Ended)' : ''}`
                          : isEventEnded
                            ? 'ENDED'
                            : `${currentEvent?.status || 'Active'} (no end date specified)`}
                      </Text>
                    </Flex>
                    <Flex align="center" gap="3" mb="3">
                      <Box as={Clock} boxSize="16px" color="volyx.textSecondary" flexShrink={0} />
                      <Text as="span" fontSize="sm" color="volyx.text">
                        {formatEventTimes(eventShifts, currentEvent?.time_intervals)}
                      </Text>
                    </Flex>
                    <Flex align="center" gap="3">
                      <Box as={Building} boxSize="16px" color="volyx.textSecondary" flexShrink={0} />
                      <Text as="span" fontSize="sm" color="volyx.text">
                        {formatDepartments(currentEvent?.departments)}
                      </Text>
                    </Flex>
                    {eventShifts.length > 0 && (
                      <Box mt="5" ref={shiftListRef} scrollMarginTop="4" data-testid="event-shift-list">
                        <Text as="h5" fontSize="md" fontWeight="semibold" color="volyx.text" mb="3">
                          Shifts
                        </Text>
                        <ShiftList shifts={eventShifts} highlightMine />
                      </Box>
                    )}
                  </>
                )}
              </Box>

              <EventPreviewOrgSection
                organization={organization}
                hideOrganizationInfo={hideOrganizationInfo}
                loading={loading}
              />

              {/* Action Buttons */}
              {!loading && !isEventEnded && (
                <Box px={{ base: '5', md: '0' }} pb={{ base: '6', md: '0' }}>
                  <Flex gap="3">
                    {!isOrgView && currentEvent?.created_by !== user?.id && !isAcceptedVolunteer && (
                      <chakra.button
                        onClick={
                          isAlreadyVolunteer
                            ? () =>
                                currentEvent?.id &&
                                requestLeave({ id: currentEvent.id, name: currentEvent.name })
                            : handleJoinEvent
                        }
                        disabled={isEventFull}
                        flex="1"
                        py="3.5"
                        borderRadius="xl"
                        fontWeight="semibold"
                        fontSize="md"
                        transition="color 0.15s, background-color 0.15s"
                        border="none"
                        cursor={isEventFull ? 'not-allowed' : 'pointer'}
                        bg={
                          isAlreadyVolunteer
                            ? 'green.500'
                            : isEventFull
                              ? 'volyx.textSecondary'
                              : 'volyx.primary'
                        }
                        color="white"
                        opacity={isEventFull && !isAlreadyVolunteer ? 0.9 : 1}
                        _hover={
                          isAlreadyVolunteer
                            ? { bg: 'green.600' }
                            : isEventFull
                              ? {}
                              : { bg: 'rgba(31,41,50,0.9)' }
                        }
                      >
                        {isAlreadyVolunteer ? 'Applied' : isEventFull ? 'Event Full' : 'Join Event'}
                      </chakra.button>
                    )}
                    {!isOrgView && currentEvent?.created_by !== user?.id && isAcceptedVolunteer && (
                      <chakra.button
                        onClick={() =>
                          currentEvent?.id &&
                          requestLeave({ id: currentEvent.id, name: currentEvent.name })
                        }
                        flex="1"
                        py="3.5"
                        borderRadius="xl"
                        fontWeight="semibold"
                        fontSize="md"
                        bg="transparent"
                        color="volyx.error"
                        border="1px solid"
                        borderColor="volyx.error"
                        cursor="pointer"
                        transition="color 0.15s, background-color 0.15s"
                        _hover={{ bg: '#FFF5F5' }}
                      >
                        Leave Event
                      </chakra.button>
                    )}
                    {currentEvent?.is_public !== false && (
                      <chakra.button
                        onClick={handleShareEvent}
                        flex="1"
                        py="3.5"
                        bg="volyx.lightGray"
                        _hover={{ bg: 'volyx.mediumGray' }}
                        borderRadius="xl"
                        fontWeight="semibold"
                        fontSize="md"
                        color="volyx.text"
                        transition="color 0.15s, background-color 0.15s"
                        border="none"
                        cursor="pointer"
                      >
                        Share Event
                      </chakra.button>
                    )}
                  </Flex>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Flex>

      <PickShiftsModal
        isOpen={pickingShifts}
        shifts={eventShifts}
        submitting={joiningShifts}
        onClose={() => setPickingShifts(false)}
        onConfirm={(shiftIds) => void confirmPickShifts(shiftIds)}
      />

      <ChangeShiftModal
        isOpen={changingFrom !== null}
        fromShift={changingFrom}
        shifts={eventShifts}
        submitting={movingShift}
        onClose={() => setChangingFrom(null)}
        onConfirm={(toShiftId) => void confirmChangeShift(toShiftId)}
      />

      <ConfirmModal
        isOpen={pendingLeaveShift != null}
        title="Leave this shift?"
        message={
          pendingLeaveShift
            ? `You will be taken off ${formatShiftLabel(pendingLeaveShift)}. The organizer is told, so they can fill your spot.`
            : ''
        }
        confirmText={leavingShift ? 'Please wait…' : 'Leave shift'}
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => void confirmLeaveShift()}
        onCancel={() => setPendingLeaveShiftId(null)}
      />

      <ConfirmModal
        isOpen={pendingEvent != null}
        title={isAcceptedVolunteer ? 'Leave Event' : 'Delete Application'}
        message={
          isAcceptedVolunteer
            ? `Are you sure you want to leave${pendingEvent?.name ? ` "${pendingEvent.name}"` : ' this event'}? You'll lose access to its chats and tasks, and your spot will be freed up.`
            : 'Are you sure you want to delete your application for this event?'
        }
        confirmText={leaving ? 'Please wait…' : isAcceptedVolunteer ? 'Leave' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => void confirmLeave()}
        onCancel={cancelLeave}
      />

      {/* Add Hours Modal */}
      {showAddHoursModal && (
        <Flex
          position="fixed"
          inset="0"
          zIndex={1100}
          align="center"
          justify="center"
          bg="blackAlpha.500"
          backdropFilter="blur(4px)"
          onClick={() => setShowAddHoursModal(false)}
        >
          <Box
            bg="white"
            borderRadius="2xl"
            w="90%"
            maxW="sm"
            p="5"
            boxShadow="2xl"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Text as="h3" fontSize="lg" fontWeight="bold" color="volyx.text" mb="1">Add hours</Text>
            <Text fontSize="xs" color="volyx.textSecondary" mb="4">
              These hours will be sent to the organization for approval.
            </Text>
            <Box mb="4">
              <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" mb="2">
                Time
              </Text>
              <DurationStepper value={addHoursMinutes} onChange={setAddHoursMinutes} />
            </Box>
            <Box mb="4">
              <chakra.label
                htmlFor={addHoursDescriptionFieldId}
                display="block"
                fontSize="xs"
                fontWeight="semibold"
                color="volyx.textSecondary"
                mb="1"
              >
                Description (optional)
              </chakra.label>
              <chakra.textarea
                id={addHoursDescriptionFieldId}
                value={addHoursDescription}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAddHoursDescription(e.target.value)}
                w="full"
                border="1px solid"
                borderColor="volyx.mediumGray"
                borderRadius="xl"
                px="3"
                py="2"
                fontSize="sm"
                outline="none"
                _focus={{ boxShadow: '0 0 0 2px #1F2932' }}
                resize="none"
                minH="70px"
                placeholder="What did you work on?"
              />
            </Box>
            <Flex gap="2" justify="flex-end">
              <chakra.button
                onClick={() => setShowAddHoursModal(false)}
                px="4"
                py="2"
                fontSize="sm"
                fontWeight="semibold"
                borderRadius="xl"
                border="1px solid"
                borderColor="volyx.mediumGray"
                color="volyx.text"
                bg="transparent"
                _hover={{ bg: 'volyx.lightGray' }}
                cursor="pointer"
              >
                Cancel
              </chakra.button>
              <chakra.button
                disabled={creatingHours}
                onClick={async () => {
                  const now = new Date();
                  const end = new Date(now.getTime() + addHoursMinutes * 60_000);
                  const sent = await createVolunteerHoursRequest({
                    minutes: addHoursMinutes,
                    description: addHoursDescription.trim() || null,
                    start_time: now.toISOString(),
                    end_time: end.toISOString(),
                  });
                  if (sent) {
                    setShowAddHoursModal(false);
                    setAddHoursMinutes(60);
                    setAddHoursDescription('');
                  }
                }}
                px="4"
                py="2"
                fontSize="sm"
                fontWeight="semibold"
                borderRadius="xl"
                bg="volyx.primary"
                color="white"
                border="none"
                _hover={{ bg: 'rgba(31,41,50,0.9)' }}
                _disabled={{ opacity: 0.6 }}
                cursor="pointer"
              >
                {creatingHours ? 'Sending...' : 'Send'}
              </chakra.button>
            </Flex>
          </Box>
        </Flex>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    </>
  );
}
